import React, { useState, useEffect } from "react";
import { ArrowRight, BarChart, Clock, Sparkles, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { useSubscriptionLimits } from "../hooks/useSubscriptionLimits";
import { SubscriptionLimitModal } from "../components/subscription/SubscriptionLimitModal";
import { motion } from "framer-motion";
import { useTranslation } from 'react-i18next';

function HomePage() {
    const navigate = useNavigate();
    const { state } = useAppContext();
    const { limits } = useSubscriptionLimits();
    const [showLimitModal, setShowLimitModal] = useState(false);
    const { t } = useTranslation();

    const [features, setFeatures] = useState([]);
    const [steps, setSteps] = useState([]);

    const handleGetStarted = () => {
        if(!state.isAuthenticated) {
            navigate('/login');
            return;
        }

        if(!limits?.isProUser && limits?.hasReachedRosterLimit) {
            setShowLimitModal(true);
            return;
        }

        navigate('/scenarios');
    };

    useEffect(() => {
        setFeatures([
            {
                icon: Clock,
                title: t('home.feature1.title'),
                description: t('home.feature1.description')
            },
            {
                icon: Users,
                title: t('home.feature2.title'),
                description: t('home.feature2.description')
            },
            {
                icon: BarChart,
                title: t('home.feature3.title'),
                description: t('home.feature3.description')
            }
        ]);

        setSteps([
            {
                title: t('home.step1.title'),
                description: t('home.step1.description'),
            },
            {
                title: t('home.step2.title'),
                description: t('home.step2.description'),
            },
            {
                title: t('home.step3.title'),
                description: t('home.step3.description'),
            },
            {
                title: t('home.step4.title'),
                description: t('home.step4.description'),
            },
            {
                title: t('home.step5.title'),
                description: t('home.step5.description'),
            },
            {
                title: t('home.step6.title'),
                description: t('home.step6.description'),
            }
        ]);
    }, [t]);

    return(
        <>
            <main className="-mt-16">
                <section className="relative bg-gradient-to-b from-gray-50 to-gray-100 overflow-hidden">
                    <div className="absolute inset-0">
                        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
                    </div>
                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-center"
                        >
                            <div className="flex justify-center mb-6 md:mb-8">
                                <div className="relative">
                                    <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full blur opacity-75 group-hover:opacity-100 transition"></div>
                                    <div className="relative bg-white rounded-full p-3 md:p-4">
                                        <Clock className="h-12 w-12 md:h-16 md:w-16 text-indigo-600"/>
                                        <Sparkles className="absolute top-0 right-0 h-6 w-6 md:h-8 md:w-8 text-indigo-400" />
                                    </div>
                                </div>
                            </div>
                            <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-6 md:mb-8 tracking-tight px-4">
                                {t('home.title')}
                            </h1>

                            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-8 md:mb-10 leading-relaxed px-4">
                                {t('home.description')}
                            </p>

                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handleGetStarted}
                                className="group bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-6 md:px-10 py-3 md:py-4 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-3 mx-auto"
                            >
                                {t('home.getStarted')}
                                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform"/>
                            </motion.button>
                        </motion.div>
                    </div>
                </section>

                <section className="bg-white py-16 md:py-24">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-16">
                            {features.map((feature, index) => (
                                <motion.div 
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.2 }}
                                    viewport={{ once: true }}
                                    className="text-center group"
                                >
                                    <feature.icon className="h-10 w-10 text-indigo-600 mx-auto mb-4"/>
                                    <h3 className="text-xl font-bold mb-4 text-gray-900">{feature.title}</h3>
                                    <p className="text-gray-600">{feature.description}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="bg-gray-100 py-16 md:py-24">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12 md:mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                {t('home.stepsTitle')}
                            </h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {steps.map((step, index) => (
                                <motion.div 
                                    key={index}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className="bg-white p-6 md:p-8 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300"
                                >
                                    <h3 className="font-bold text-lg text-gray-900 mb-4">{step.title}</h3>
                                    <p className="text-gray-600">{step.description}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>
            </main>

            <SubscriptionLimitModal 
                isOpen={showLimitModal}
                onClose={() => setShowLimitModal(false)}
                feature="rosters"
                limit={3}
            />
        </>
    );
}

export default HomePage;